import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import qs from 'qs';
import { request } from '../../common/util/RestClient';
import envConfig from '../../config';
import {
  createWebhook,
  deleteWebhook,
  editWebhook,
  editWebhookStatus,
  fetchWebhookActivities,
  fetchWebhookById,
  fetchWebhooks,
  testWebhook,
} from '../routines/webhookRoutines';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import { GLOBAL_DEFAULT_PAGE_SIZE } from '../../common/constants/CommonConstants';
import { convertFilterToVfl } from '../../common/util/velarisFilterLanguageUtils';
import { RESPONSE_STATUS_TEXT } from '../../pages/DataManagement/WebhookManagement/webhookConstants';
import { convertLocalDateTimeToUTCInFilters } from '../../common/util/filterUtil';

const WEBHOOK_MANAGEMENT_BASE_URL = `${envConfig.APIG_URL}webhook-management`;

function* fetchWebhooksSaga() {
  try {
    const url = `${WEBHOOK_MANAGEMENT_BASE_URL}/webhooks`;
    const response = yield call(request, url);
    yield put(fetchWebhooks.success(response.data));
  } catch (error) {
    yield put(fetchWebhooks.failure({ error }));
  } finally {
    yield put(fetchWebhooks.fulfill());
  }
}

function* fetchWebhookByIdSaga({ payload }) {
  try {
    const { webhookId } = payload;
    const url = `${WEBHOOK_MANAGEMENT_BASE_URL}/webhooks/${webhookId}`;
    const response = yield call(request, url);
    yield put(fetchWebhookById.success(response.data));
  } catch (error) {
    yield put(fetchWebhookById.failure({ error }));
  }
}

function* fetchWebhookActivitiesSaga({ payload }) {
  try {
    const {
      webhookId,
      pageSize,
      page,
      sortColumn,
      sortDirection,
      filters,
      query,
    } = payload;
    const params = {};
    if (query) {
      params.query = query;
    }
    if (sortColumn && sortDirection) {
      params.sortColumn = sortColumn;
      params.sortDirection = sortDirection;
    }
    if (filters && filters.length > 0) {
      params.filters = convertFilterToVfl(
        convertLocalDateTimeToUTCInFilters(filters)
      );
    }
    const paramsString = qs.stringify(params);
    const mandatoryParams = `page=${page || 1}&pageSize=${
      pageSize || GLOBAL_DEFAULT_PAGE_SIZE
    }`;
    const url = `${WEBHOOK_MANAGEMENT_BASE_URL}/webhooks/${webhookId}/activity?${mandatoryParams}&${paramsString}`;
    const response = yield call(request, url);
    yield put(fetchWebhookActivities.success(response));
  } catch (error) {
    yield put(fetchWebhookActivities.failure({ error }));
  }
}

function* createWebhookSaga({ payload }) {
  try {
    const url = `${WEBHOOK_MANAGEMENT_BASE_URL}/webhooks`;
    const config = {
      method: 'POST',
      payload,
    };
    yield call(request, url, config);
    yield put(
      createWebhook.success({
        successMessage: SUCCESS_MESSAGES.CREATE_WEBHOOK,
      })
    );

    yield put(fetchWebhooks.trigger());
  } catch (error) {
    yield put(createWebhook.failure({ error }));
  } finally {
    yield put(createWebhook.fulfill());
  }
}

function* editWebhookSaga({ payload }) {
  try {
    const { webhookId, ...rest } = payload;
    const url = `${WEBHOOK_MANAGEMENT_BASE_URL}/webhooks/${webhookId}`;
    const config = {
      method: 'PUT',
      payload: { ...rest },
    };
    yield call(request, url, config);
    yield put(
      editWebhook.success({
        successMessage: SUCCESS_MESSAGES.EDIT_WEBHOOK,
      })
    );
    yield put(fetchWebhooks.trigger());
  } catch (error) {
    yield put(editWebhook.failure({ error }));
  } finally {
    yield put(editWebhook.fulfill());
  }
}

function* deleteWebhookSaga({ payload }) {
  try {
    const { webhookId } = payload;
    const url = `${WEBHOOK_MANAGEMENT_BASE_URL}/webhooks/${webhookId}`;
    const config = {
      method: 'DELETE',
    };
    yield call(request, url, config);
    yield put(
      deleteWebhook.success({
        successMessage: SUCCESS_MESSAGES.DELETE_WEBHOOK,
      })
    );

    yield put(fetchWebhooks.trigger());
  } catch (error) {
    yield put(deleteWebhook.failure({ error }));
  } finally {
    yield put(deleteWebhook.fulfill());
  }
}

function* editWebhookStatusSaga({ payload }) {
  try {
    const { webhookId, status } = payload;
    const url = `${WEBHOOK_MANAGEMENT_BASE_URL}/webhooks/${webhookId}/status`;
    const config = {
      method: 'POST',
      payload: {
        status,
      },
    };
    yield call(request, url, config);
    yield put(
      editWebhookStatus.success({
        successMessage:
          Number(status) === 1
            ? SUCCESS_MESSAGES.ACTIVATE_WEBHOOK
            : SUCCESS_MESSAGES.DEACTIVATE_WEBHOOK,
      })
    );

    yield put(fetchWebhooks.trigger());
  } catch (error) {
    yield put(editWebhookStatus.failure({ error }));
  } finally {
    yield put(editWebhookStatus.fulfill());
  }
}
function* testWebhookSaga({ payload }) {
  try {
    const url = `${WEBHOOK_MANAGEMENT_BASE_URL}/webhooks/test`;
    const config = {
      method: 'POST',
      payload,
    };
    const { data } = yield call(request, url, config);
    if (data.status === RESPONSE_STATUS_TEXT.SUCCESS) {
      yield put(testWebhook.success({}));
    } else {
      yield put(
        testWebhook.failure({
          noAlert: true,
          error: 'Failed to send Sample Data to the Endpoint URL',
        })
      );
    }
  } catch (error) {
    yield put(testWebhook.failure({ error }));
  } finally {
    yield put(testWebhook.fulfill());
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchWebhooks.TRIGGER, fetchWebhooksSaga),
    takeLatest(fetchWebhookById.TRIGGER, fetchWebhookByIdSaga),
    takeLatest(fetchWebhookActivities.TRIGGER, fetchWebhookActivitiesSaga),
    takeEvery(testWebhook.TRIGGER, testWebhookSaga),
    takeEvery(editWebhookStatus.TRIGGER, editWebhookStatusSaga),
    takeEvery(editWebhook.TRIGGER, editWebhookSaga),
    takeEvery(createWebhook.TRIGGER, createWebhookSaga),
    takeEvery(deleteWebhook.TRIGGER, deleteWebhookSaga),
  ]);
}
